// file: src/utils/excelDataCreator.js
import { formatTanggalExcel, convertToExcelDate, convertToNumber, parseBibit } from './excelDataFetcher';

// Fungsi untuk memformat tanggal dari format YYYY-MM-DD ke Excel-friendly
const formatAwalRawat = (tanggal) => {
  if (!tanggal || tanggal === '-') return '-';
  
  try {
    // Jika tanggal sudah dalam format YYYY-MM-DD (dari Firebase)
    if (tanggal.includes('-') && tanggal.split('-')[0].length === 4) {
      const [year, month, day] = tanggal.split('-');
      return `${day}/${month}/${year}`; // Format: 08/11/2024
    }
    // Jika sudah dalam format DD-MM-YYYY, gunakan formatTanggalExcel biasa
    return formatTanggalExcel(tanggal);
  } catch (error) {
    console.error('Error formatting awal rawat:', error);
  }
  return '-';
};

// Fungsi untuk membuat data Excel
export const createExcelData = (groupData, lokasiData) => {
  const excelData = [];

  // Header utama (diperluas hingga kolom BC)
  excelData.push([
    'TANGGAL PENGAMATAN', '', '', 'LOKASI', 'PLOT', 'STATUS', 'BIBIT', '', '', 'LUAS', 'AWAL RAWAT', 'PJP',
    '', // M1 (kosong)
    'POPULASI', '', // N1-O1
    '', // P1 (kosong)
    'FASE BUNGA NORMAL', '', '', '', '', '', // Q1-V1
    'FASE BUNGA ALAMI', '', '', '', '', '', '', '', '', '', // W1-AF1
    'BERBUNGA', '', '', '', // AG1-AJ1
    'BUAH ALAMI', // AK1
    'MATI', // AL1
    'MANDUL', '', '', '', // AM1-AP1
    'TOTAL', // AQ1
    'SPOT MATI', // AR1
    'BERBUNGA', '', '', '', // AS1-AV1 (Pinggiran)
    'BUAH ALAMI', // AW1
    'MATI', // AX1
    'MANDUL', '', '', '', // AY1-BB1
    'TOTAL' // BC1
  ]);
  
  excelData.push([
    '', '', '', '', '', '', 'VARIETAS', 'KELAS', 'JENIS', '', '', '',
    '', // M2 (kosong)
    'NORMAL', 'TOTAL', // N2-O2
    '', // P2 (kosong)
    'OPEN HEART', 'RED BUD 1.25', 'RED BUD 2.54', 'EARLY CONES', 'MID CONES', 'LATE CONES', // Q2-V2
    'EARLY PETALS', 'MID PETALS', 'LATE PETALS', 'DRY PETALS', 'FILLING STAGE 1', 'FILLING STAGE 2', 'SC0', 'SC0>', 'TERPETIK', 'DIMAKAN TIKUS', // W2-AF2
    'NORMAL', 'PENYAKIT', '', 'KERDIL', // AG2-AJ2
    '', // AK2
    '', // AL2
    'NORMAL', 'PENYAKIT', '', 'KERDIL', // AM2-AP2
    '', // AQ2
    '', // AR2
    'NORMAL', 'PENYAKIT', '', 'KERDIL', // AS2-AV2 (Pinggiran)
    '', // AW2
    '', // AX2
    'NORMAL', 'PENYAKIT', '', 'KERDIL', // AY2-BB2
    '' // BC2
  ]);
  
  excelData.push([
    '', '', '', '', '', '', '', '', '', '', '', '',
    '', // M3 (kosong)
    '', '', // N3-O3
    '', // P3 (kosong)
    '', '', '', '', '', '', // Q3-V3
    '', '', '', '', '', '', '', '', '', '', // W3-AF3
    '', 'PHY', 'ERW', '', // AG3-AJ3
    '', // AK3
    '', // AL3
    '', 'PHY', 'ERW', '', // AM3-AP3
    '', // AQ3
    '', // AR3
    '', 'PHY', 'ERW', '', // AS3-AV3 (Pinggiran)
    '', // AW3
    '', // AX3
    '', 'PHY', 'ERW', '', // AY3-BB3
    '' // BC3
  ]);

  // Process setiap item dalam groupData
  for (const item of groupData.items) {
    if (item.originalData && item.originalData.plot) {
      // Process setiap plot dalam item
      for (const [plotKey, plot] of Object.entries(item.originalData.plot)) {
        if (plot.data_entry) {
          const bibitParsed = parseBibit(lokasiData?.bibit);
          
          // Format tanggal dan luas sesuai dengan excelDownload.js
          const tanggalPengamatan = formatTanggalExcel(item.tanggal_pengamatan);
          const awalRawat = formatAwalRawat(lokasiData?.mulai_rawat);
          const luasPlot = convertToNumber(plot.luas_plot);
          
          const rowData = [
            // Tanggal Pengamatan (A) - format Excel-friendly
            tanggalPengamatan,
            '',
            '',
            // Lokasi (D)
            item.lokasi || '',
            // Plot (E)
            plot.plot || '',
            // Status (F) - ambil dari tb_lokasi
            lokasiData?.status || '',
            // Bibit - dipecah menjadi 3 kolom (G, H, I)
            bibitParsed.varietas,
            bibitParsed.kelas,
            bibitParsed.jenis,
            // Luas (J) - format number
            luasPlot,
            // Awal Rawat (K) - format Excel-friendly
            awalRawat,
            // PJP (L)
            plot.pjp || '',
            // Kolom M (kosong)
            '',
            // Populasi - Normal (N) - integer
            convertToNumber(plot.data_entry.populasi?.normal_pb),
            // Populasi - Total (O) - integer
            convertToNumber(plot.data_entry.populasi?.total_pb),
            // Kolom P (kosong)
            '',
            // Fase Bunga Normal - integer columns
            convertToNumber(plot.data_entry.fase_bunga_normal?.open_heart_fbn_pb),
            convertToNumber(plot.data_entry.fase_bunga_normal?.red_bud_125_fbn_pb),
            convertToNumber(plot.data_entry.fase_bunga_normal?.red_bud_254_fbn_pb),
            convertToNumber(plot.data_entry.fase_bunga_normal?.early_cones_fbn_pb),
            convertToNumber(plot.data_entry.fase_bunga_normal?.mid_cones_fbn_pb),
            convertToNumber(plot.data_entry.fase_bunga_normal?.late_cones_fbn_pb),
            // Fase Bunga Alami - integer columns
            convertToNumber(plot.data_entry.fase_bunga_alami?.early_petals_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.mid_petals_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.late_petals_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.dry_petals_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.filling_stage_1_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.filling_stage_2_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.sc0_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.sc0_ld_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.terpetik_fba_pb),
            convertToNumber(plot.data_entry.fase_bunga_alami?.dimakan_tikus_fba_pb),
            // Berbunga - integer columns
            convertToNumber(plot.data_entry.berbunga?.normal_bb_pb),
            convertToNumber(plot.data_entry.berbunga?.penyakit_berbunga?.phy_pbb_pb),
            convertToNumber(plot.data_entry.berbunga?.penyakit_berbunga?.erw_pbb_pb),
            convertToNumber(plot.data_entry.berbunga?.kerdil_bb_pb),
            // Buah Alami - integer
            convertToNumber(plot.data_entry.buah_alami_pb),
            // Mati - integer
            convertToNumber(plot.data_entry.mati_pb),
            // Mandul - integer columns
            convertToNumber(plot.data_entry.mandul?.normal_m_pb),
            convertToNumber(plot.data_entry.mandul?.penyakit_mandul?.phy_pm_pb),
            convertToNumber(plot.data_entry.mandul?.penyakit_mandul?.erw_pm_pb),
            convertToNumber(plot.data_entry.mandul?.kerdil_m_pb),
            // Total - integer
            convertToNumber(plot.data_entry.total_pb),
            // Spot Mati - integer
            convertToNumber(plot.data_entry.spot_mati_pb),
            // Pinggiran - Berbunga - integer columns
            convertToNumber(plot.data_entry.berbunga_pinggiran?.normal_bbp_pb),
            convertToNumber(plot.data_entry.berbunga_pinggiran?.penyakit_berbunga_pinggiran?.phy_pbbp_pb),
            convertToNumber(plot.data_entry.berbunga_pinggiran?.penyakit_berbunga_pinggiran?.erw_pbbp_pb),
            convertToNumber(plot.data_entry.berbunga_pinggiran?.kerdil_bbp_pb),
            // Pinggiran - Buah Alami - integer
            convertToNumber(plot.data_entry.buah_alami_p_pb),
            // Pinggiran - Mati - integer
            convertToNumber(plot.data_entry.mati_p_pb),
            // Pinggiran - Mandul - integer columns
            convertToNumber(plot.data_entry.mandul_pinggiran?.normal_mp_pb),
            convertToNumber(plot.data_entry.mandul_pinggiran?.penyakit_mandul_pinggiran?.phy_pmp_pb),
            convertToNumber(plot.data_entry.mandul_pinggiran?.penyakit_mandul_pinggiran?.erw_pmp_pb),
            convertToNumber(plot.data_entry.mandul_pinggiran?.kerdil_mp_pb),
            // Pinggiran - Total - integer
            convertToNumber(plot.data_entry.total_p_pb)
          ];
          
          excelData.push(rowData);
        }
      }
    }
  }

  return excelData;
};